import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, Images, LogOut, ShieldCheck, Sparkles, Trash2, UserRound, Zap } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { planName } from "../../shared/products";
import { api } from "../api";
import { useAuth } from "../auth";
import { useCapabilities } from "../capabilities";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PageHeader } from "../components/PageHeader";

export function AccountPage() {
  const { me, isLoading, requestLogin } = useAuth();
  const { fubeiEnabled } = useCapabilities();
  const [deleting, setDeleting] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const logout = useMutation({
    mutationFn: api.logout,
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ["me"] }); navigate("/generate"); },
  });
  const deleteAccount = useMutation({
    mutationFn: api.deleteAccount,
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ["me"] }); navigate("/generate"); },
  });

  if (isLoading) return <div className="account-loading skeleton" />;
  if (!me) {
    return <section><PageHeader eyebrow="Creator Profile" title="个人中心" subtitle="登录后查看用量、作品与账户信息。" /><div className="empty-state guest-state"><span className="kook-mark">K</span><h2>连接你的 KOOK 账号</h2><p>GAMEFRAME 只使用 OAuth 读取基础身份信息。</p><button className="button button-primary" onClick={() => requestLogin("/account")}>使用 KOOK 登录</button></div></section>;
  }

  const { user, quota, stats } = me;
  const percent = Math.min(100, Math.round((quota.used / Math.max(1, quota.total)) * 100));

  return (
    <section>
      <PageHeader eyebrow="Creator Profile" title="个人中心" subtitle="用量、账户与权益状态在一个页面完成管理。" />
      <div className="profile-hero card">
        <div className="profile-avatar">{user.avatarUrl ? <img src={user.avatarUrl} alt="" /> : user.nickname.slice(0, 1).toUpperCase()}</div>
        <div><p className="eyebrow">KOOK Identity</p><h2>{user.nickname}</h2><p>@{user.username} · KOOK OAuth</p></div>
        <span className={user.planCode !== "FREE" ? "status-pill creator" : "status-pill"}>{planName(user.planCode).replace(" 30 天权益包", "")}</span>
      </div>

      <div className="stats-grid">
        <article className="stat-card"><span><Sparkles size={17} /></span><strong>{quota.used}</strong><small>本期已生成</small></article>
        <article className="stat-card"><span><Images size={17} /></span><strong>{stats.artworks}</strong><small>已保存作品</small></article>
        <article className="stat-card"><span><Zap size={17} /></span><strong>{quota.available}</strong><small>剩余次数</small></article>
      </div>

      <div className="profile-grid">
        <article className="card usage-card">
          <div className="card-heading"><div><p className="eyebrow">Usage</p><h2>生成额度</h2></div><b>{percent}% 已使用</b></div>
          <div className="large-quota-track"><i style={{ width: percent + "%" }} /></div>
          <div className="usage-numbers"><div><strong>{quota.used}</strong><small>已使用</small></div><div><strong>{quota.reserved}</strong><small>生成中</small></div><div><strong>{quota.available}</strong><small>可使用</small></div></div>
          <p className="period-copy"><CalendarDays size={15} />当前周期：{formatDate(quota.periodStart)} — {formatDate(quota.periodEnd)}</p>
          {fubeiEnabled && user.planCode === "FREE" && <Link className="button button-primary" to="/pricing"><Zap size={17} />查看创作者权益</Link>}
        </article>

        <aside className="card account-card">
          <div className="card-heading"><div><p className="eyebrow">Account</p><h2>账户与权益</h2></div><ShieldCheck size={20} /></div>
          <AccountRow label="KOOK 昵称" value={user.nickname} />
          <AccountRow label="KOOK 用户名" value={user.username} />
          <AccountRow label="登录方式" value="KOOK OAuth" />
          <AccountRow label="当前方案" value={planName(user.planCode)} accent />
          <AccountRow label="权益到期" value={user.planEndsAt ? formatDate(user.planEndsAt) : "—"} />
          <p className="account-note">KOOK 身份信息为只读，会在下次授权时同步。创作者权益为一次性购买，不会自动续费。</p>
        </aside>
      </div>

      <div className="account-actions card">
        <div><h2>账户操作</h2><p>退出只会结束当前 GAMEFRAME 会话；注销会清除作品与身份资料，并匿名化依法需保留的订单记录。</p></div>
        <div><button className="button button-secondary" onClick={() => logout.mutate()} disabled={logout.isPending}><LogOut size={17} />退出登录</button><button className="button button-danger-subtle" onClick={() => setDeleting(true)}><Trash2 size={17} />注销账户</button></div>
      </div>
      <footer className="account-footer"><a href="https://www.kookapp.cn/refund/alipays/#zfb" target="_blank" rel="noreferrer">售后服务</a><Link to="/terms">用户协议</Link><Link to="/privacy">隐私政策</Link></footer>
      <ConfirmDialog open={deleting} title="确认注销 GAMEFRAME 账户？" description="此操作会删除已生成作品与本地身份资料，订单记录仅按法定义务匿名保留。之后可用同一 KOOK 账号重新注册为空账户，操作不可撤销。" confirmLabel="确认注销" tone="danger" busy={deleteAccount.isPending} onClose={() => setDeleting(false)} onConfirm={() => deleteAccount.mutate()} />
    </section>
  );
}

function AccountRow({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return <div className="account-row"><span>{label}</span><strong className={accent ? "accent" : undefined}>{value}</strong></div>;
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(value));
}
